import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  PasswordInput,
  SegmentedControl,
  Select,
  Slider,
  Switch,
  TagsInput,
  Text,
  Textarea,
} from "@mantine/core";
import type { Setting } from "../api/types";
import { ArtworkProviders } from "./ArtworkProviders";
import { TelegramLogin, VkLogin } from "./PublishLogin";
import { PublishTargets } from "./PublishTargets";
import { ResetChip, SettingShell, STATIC_VALUE, useSaveSetting } from "./SettingShell";
import { TypedRow } from "./TypedRow";
import { fieldFor, joinList, render, splitList, timeZones, type Field } from "./settingsFields";
import { Chip } from "../ui/Chip";
import { StatePill } from "../ui/StatePill";

/**
 * One settings row: what it is called, the control its value actually is, what it does.
 *
 * The control is not chosen here. `fieldFor` reads it off the widget table and the value's
 * own shape, so a key gains a slider or a byte box by being listed in `settingsFields.ts`
 * rather than by growing a branch in this file.
 *
 * Two save contracts, and which one a row gets follows from how you operate it. A value you
 * *type* keeps a local draft and commits on Enter or the Save chip, because every keystroke
 * on the way to "300" is a value the server would have taken and stored. A value you *pick*
 * -- a switch, an option, a tag, a slider, a zone -- has no half-typed state, so it saves on
 * the change itself and there is no chip to hunt for.
 */

// The fields publish/media.MESSAGE_FIELDS substitutes, minus {note}, which only VK fills.
const TEMPLATE_FIELDS =
  "{performer}, {djs}, {guests}, {show}, {marker}, {place}, {location}, {aired}, " +
  "{date}, {start}, {end}, {tz}, {listen}, {url}";

export function SettingRow({ setting }: { setting: Setting }) {
  // A few keys are a shape no generic control can express honestly. An ordered list of
  // providers typed as JSON would be editable in principle and unusable in practice.
  if (setting.key === "artwork_providers")
    return (
      <SettingShell setting={setting}>
        <ArtworkProviders setting={setting} />
      </SettingShell>
    );
  if (setting.key === "publish_targets")
    return (
      <SettingShell setting={setting}>
        <PublishTargets setting={setting} />
      </SettingShell>
    );
  // The session keys get their sign-in rather than a raw password box.
  if (setting.key === "telegram_session")
    return (
      <SettingShell setting={setting}>
        <TelegramLogin setting={setting} />
      </SettingShell>
    );
  if (setting.key === "vk_session_cookies")
    return (
      <SettingShell setting={setting}>
        <VkLogin />
      </SettingShell>
    );
  // A post body is several lines of text with its own field list.
  if (setting.key === "telegram_message_template" || setting.key === "vk_message_template")
    return <TemplateSettingRow setting={setting} />;
  if (!setting.editable) return <ReadOnlySettingRow setting={setting} />;
  if (setting.is_secret) return <SecretSettingRow setting={setting} />;

  const field = fieldFor(setting);
  switch (field.kind) {
    case "switch":
      return <SwitchRow setting={setting} />;
    case "checkbox":
      return <CheckboxRow setting={setting} />;
    case "options":
      return <OptionsRow setting={setting} />;
    case "slider":
      return <SliderRow setting={setting} field={field} />;
    case "tags":
      return <TagsRow setting={setting} />;
    case "timezone":
      return <TimezoneRow setting={setting} />;
    case "epoch":
      return <EpochRow setting={setting} />;
    default:
      return <TypedRow setting={setting} field={field} />;
  }
}

function ReadOnlySettingRow({ setting }: { setting: Setting }) {
  const { t } = useTranslation("admin");
  return (
    <SettingShell
      setting={setting}
      actions={
        <StatePill tone="quiet" title={t("settingsPage.environmentTitle")}>
          {t("settingsPage.environment")}
        </StatePill>
      }
    >
      <Box
        component="span"
        // No row in the database: the value comes from the environment or the built-in
        // default. Saying so stops "reset" looking like it did nothing when the value was
        // never overridden in the first place.
        c={setting.source !== "db" ? "dimmed" : undefined}
        style={STATIC_VALUE}
      >
        {setting.is_secret
          ? setting.is_set
            ? t("settingsPage.set")
            : t("settingsPage.notSet")
          : render(setting.value)}
      </Box>
    </SettingShell>
  );
}

/**
 * A password field for a value the server will not send back.
 *
 * The draft starts empty and is cleared after a save, because there is nothing to show: the
 * only honest states are "one is set" and "one is not", and offering to replace it is the
 * only edit worth making.
 */
function SecretSettingRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  const [draft, setDraft] = useState("");
  const { t } = useTranslation("admin");

  const submit = async () => {
    if (await save(draft)) setDraft("");
  };

  return (
    <SettingShell
      setting={setting}
      error={error}
      actions={
        <>
          {draft !== "" && (
            <Chip variant="accent" onClick={() => void submit()}>
              {t("common.save")}
            </Chip>
          )}
          <ResetChip setting={setting} onReset={() => setDraft("")} />
        </>
      }
    >
      <PasswordInput
        id={`setting-${setting.key}`}
        autoComplete="new-password"
        value={draft}
        placeholder={setting.is_set ? t("settingsPage.replaceSecret") : t("settingsPage.notSet")}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") void submit();
          if (event.key === "Escape") setDraft("");
        }}
      />
    </SettingShell>
  );
}

function SwitchRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  return (
    <SettingShell setting={setting} error={error} actions={<ResetChip setting={setting} />}>
      <Switch
        id={`setting-${setting.key}`}
        mt={6}
        checked={setting.value === true}
        onChange={(event) => void save(event.currentTarget.checked)}
      />
    </SettingShell>
  );
}

function CheckboxRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  return (
    <SettingShell setting={setting} error={error} actions={<ResetChip setting={setting} />}>
      <Checkbox
        id={`setting-${setting.key}`}
        mt={6}
        checked={setting.value === true}
        onChange={(event) => void save(event.currentTarget.checked)}
      />
    </SettingShell>
  );
}

/**
 * The value is one of a fixed few, and the server is the one that says which.
 *
 * A short handful reads better laid out than folded into a menu, so `mail_backend` and
 * `show_key_mode` come out segmented and `log_level` a picker -- decided by the shape of the
 * list rather than by naming either key here.
 */
function OptionsRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  const { t } = useTranslation("admin");
  const options = setting.options ?? [];
  const laidOut = options.length <= 3 && options.join("").length <= 24;
  const value = String(setting.value ?? "");

  return (
    <SettingShell setting={setting} error={error} actions={<ResetChip setting={setting} />}>
      {laidOut ? (
        <SegmentedControl
          id={`setting-${setting.key}`}
          aria-label={t(`settingsPage.label.${setting.key}`, { defaultValue: setting.key })}
          value={value}
          data={options}
          onChange={(next) => void save(next)}
        />
      ) : (
        <Select
          id={`setting-${setting.key}`}
          value={value}
          data={options}
          allowDeselect={false}
          onChange={(next) => next !== null && void save(next)}
        />
      )}
    </SettingShell>
  );
}

/** A 0..1 ratio. Committed on release, so one drag is one write and one audit entry. */
function SliderRow({ setting, field }: { setting: Setting; field: Field }) {
  const { save, error } = useSaveSetting(setting.key);
  const { t } = useTranslation("admin");
  const min = field.min ?? 0;
  const max = field.max ?? 1;
  const stored = Number(setting.value);
  // The server is the authority: a refusal, or a reset, has to move the handle back. So the
  // handle is drawn from the stored value, and a drag is an override tagged with the value it
  // started from -- which the next stored value it does not match simply retires.
  const [drag, setDrag] = useState<{ base: number; value: number } | null>(null);
  const draft = drag && drag.base === stored ? drag.value : stored;

  return (
    <SettingShell
      setting={setting}
      error={error}
      actions={
        <ResetChip
          setting={setting}
          onReset={(fresh) => setDrag({ base: stored, value: Number(fresh.value) })}
        />
      }
    >
      <Slider
        id={`setting-${setting.key}`}
        aria-label={t(`settingsPage.label.${setting.key}`, { defaultValue: setting.key })}
        mt={10}
        mb={22}
        min={min}
        max={max}
        step={field.step}
        value={draft}
        marks={[{ value: min, label: min }, { value: (min + max) / 2 }, { value: max, label: max }]}
        onChange={(next) => setDrag({ base: stored, value: next })}
        onChangeEnd={(next) => void save(next)}
      />
    </SettingShell>
  );
}

/**
 * A list of strings as pills.
 *
 * Two storage shapes reach this one row: `live_markers` is a real JSON array and
 * `instance_priority` a comma-joined string, so what it sends back is decided by what it was
 * given rather than by naming either key.
 */
function TagsRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  const stored = Array.isArray(setting.value);
  // Optimistic while the write is in flight, and reseeded from the server afterwards -- so a
  // list the server refuses snaps back with its reason on screen rather than staying wrong.
  // The typed list is tagged with the server value it was typed over, so a fresh one arriving
  // retires it without an effect to do the reseeding.
  const [pending, setPending] = useState<{ base: unknown; tags: string[] } | null>(null);
  const tags = pending && pending.base === setting.value ? pending.tags : readTags(setting.value);

  return (
    <SettingShell setting={setting} error={error} actions={<ResetChip setting={setting} />}>
      <TagsInput
        id={`setting-${setting.key}`}
        value={tags}
        onChange={(next) => {
          setPending({ base: setting.value, tags: next });
          // A refusal never invalidates the query, so nothing else would put the pills back:
          // the row would keep showing a list the server does not hold.
          void save(stored ? next : joinList(next)).then((ok) => {
            if (!ok) setPending(null);
          });
        }}
      />
    </SettingShell>
  );
}

function readTags(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  return splitList(typeof value === "string" ? value : "");
}

/** Every zone the browser's own ICU knows, searched rather than typed. */
function TimezoneRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  const current = String(setting.value ?? "");
  return (
    <SettingShell setting={setting} error={error} actions={<ResetChip setting={setting} />}>
      <Select
        id={`setting-${setting.key}`}
        searchable
        allowDeselect={false}
        value={current}
        data={timeZones(current)}
        onChange={(next) => next !== null && void save(next)}
      />
    </SettingShell>
  );
}

/** Unix seconds the sign-in wrote. Read-only in effect: nothing sensible is typed here. */
function EpochRow({ setting }: { setting: Setting }) {
  const { t } = useTranslation("admin");
  const seconds = Number(setting.value) || 0;
  return (
    <SettingShell setting={setting} actions={<ResetChip setting={setting} />}>
      <Box component="span" c="dimmed" style={STATIC_VALUE}>
        {seconds
          ? t("settingsPage.tokenExpires", { when: new Date(seconds * 1000).toLocaleString() })
          : t("settingsPage.tokenNotSet")}
      </Box>
    </SettingShell>
  );
}

/** A post body: several lines, its own field list, and no save-on-Enter. */
function TemplateSettingRow({ setting }: { setting: Setting }) {
  const { save, error } = useSaveSetting(setting.key);
  const stored = render(setting.value);
  const [draft, setDraft] = useState(stored);
  const { t } = useTranslation("admin");

  return (
    <SettingShell
      setting={setting}
      error={error}
      hint={
        // Which fields the template may name, under the description rather than in the
        // group's blurb: it is reference material you read while typing.
        <Text size="xs" c="dimmed" mt={4}>
          {t(
            setting.key === "telegram_message_template"
              ? "settingsPage.templateHintTelegram"
              : "settingsPage.templateHintVk",
            { fields: TEMPLATE_FIELDS },
          )}
        </Text>
      }
      actions={
        <>
          {draft !== stored && (
            <Chip variant="accent" onClick={() => void save(draft)}>
              {t("common.save")}
            </Chip>
          )}
          <ResetChip setting={setting} onReset={(fresh) => setDraft(render(fresh.value))} />
        </>
      }
    >
      <Textarea
        id={`setting-${setting.key}`}
        autosize
        minRows={6}
        spellCheck={false}
        value={draft}
        styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)", fontSize: 12 } }}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") setDraft(stored);
        }}
      />
    </SettingShell>
  );
}
